import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { CircularProgress, Drawer, IconButton, Tooltip } from '@mui/material'
import { FolderClock, RefreshCw, X } from 'lucide-react'
import Button from '../ui/Button'
import { formatDateTime } from '../../lib/utils'
import { fetchProjectBranches, type ProjectBranch } from '../../services/projectService'

type ProjectFilter = 'all' | 'active' | 'merged' | 'cancelled'

interface ProjectsHistoryDrawerProps {
  open: boolean
  codeIndexId?: string
  projects?: ProjectBranch[]
  onProjectsLoaded?: (projects: ProjectBranch[]) => void
  onClose: () => void
  onEnterProject: (project: ProjectBranch) => void
}

function isCompletedProject(project: ProjectBranch): boolean {
  return isMergedProject(project) || isCancelledProject(project)
}

function isMergedProject(project: ProjectBranch): boolean {
  return String(project.status || '') === 'Merged'
}

function isCancelledProject(project: ProjectBranch): boolean {
  return String(project.status || '') === 'Cancelled'
}

function isActiveProject(project: ProjectBranch): boolean {
  return !isCompletedProject(project)
}

function isMainProject(project: ProjectBranch): boolean {
  return String(project.name || '').trim().toLowerCase() === 'main'
}

function formatStatusLabel(status: string | null | undefined): string {
  const value = String(status || '').trim()
  if (value === 'Cancelled') return 'Cancelled'
  if (value === 'Merged') return 'Merged'
  return 'In progress'
}

function getStatusClassName(project: ProjectBranch): string {
  if (isCancelledProject(project)) {
    return 'bg-gray-100 text-gray-600'
  }

  if (isMergedProject(project)) {
    return 'bg-[#E7F5EC] text-[#1F7A3F]'
  }

  return 'bg-[#E9F2FB] text-[#1D5176]'
}

function getProjectTimestampDetails(project: ProjectBranch): { label: string; value: string } {
  if (isCancelledProject(project)) {
    return { label: 'Cancelled at', value: formatDateTime(project.cancelled || '') || '--' }
  }
  if (isMergedProject(project)) {
    return { label: 'Merged at', value: formatDateTime(project.merged || '') || '--' }
  }
  return { label: 'Started', value: formatDateTime(project.createdAt || '') || '--' }
}

function getProjectCreator(project: ProjectBranch): string {
  return project.createdBy?.name || project.createdBy?.emailAddress || '--'
}

function getProjectChanges(project: ProjectBranch): string | null {
  const counts = [
    { label: 'approved', value: project.changes?.approved || 0 },
    { label: 'pending', value: project.changes?.pending || 0 },
    { label: 'rejected', value: project.changes?.rejected || 0 },
  ].filter(({ value }) => value > 0)

  return counts.length > 0
    ? counts.map(({ label, value }) => `${value} ${label}`).join(' · ')
    : null
}

export default function ProjectsHistoryDrawer({
  open,
  codeIndexId,
  projects: providedProjects,
  onProjectsLoaded,
  onClose,
  onEnterProject,
}: ProjectsHistoryDrawerProps) {
  const [loadedProjects, setLoadedProjects] = useState<ProjectBranch[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<ProjectFilter>('all')
  const projects = providedProjects ?? loadedProjects
  const visibleProjects = useMemo(() => projects.filter(project => !isMainProject(project)), [projects])

  const loadProjects = useCallback(async () => {
    if (!codeIndexId) return

    setLoading(true)
    setError(null)
    try {
      const nextProjects = await fetchProjectBranches(codeIndexId)
      setLoadedProjects(nextProjects)
      onProjectsLoaded?.(nextProjects)
    } catch (err) {
      console.error('Failed to load projects.', err)
      setError('Unable to load projects.')
    } finally {
      setLoading(false)
    }
  }, [codeIndexId, onProjectsLoaded])

  useEffect(() => {
    if (open) {
      void loadProjects()
      setFilter('all')
    }
  }, [loadProjects, open])

  const counts = useMemo(() => ({
    all: visibleProjects.length,
    active: visibleProjects.filter(isActiveProject).length,
    merged: visibleProjects.filter(isMergedProject).length,
    cancelled: visibleProjects.filter(isCancelledProject).length,
  }), [visibleProjects])

  const filteredProjects = useMemo(() => {
    if (filter === 'active') return visibleProjects.filter(isActiveProject)
    if (filter === 'merged') return visibleProjects.filter(isMergedProject)
    if (filter === 'cancelled') return visibleProjects.filter(isCancelledProject)
    return visibleProjects
  }, [filter, visibleProjects])

  const filterOptions: Array<{ value: ProjectFilter; label: string; count: number }> = [
    { value: 'all', label: 'All', count: counts.all },
    { value: 'active', label: 'In progress', count: counts.active },
    { value: 'merged', label: 'Merged', count: counts.merged },
    { value: 'cancelled', label: 'Cancelled', count: counts.cancelled },
  ]

  if (!open) return null

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ '& .MuiDrawer-paper': { width: '34vw', minWidth: 380, maxWidth: 560, boxSizing: 'border-box' } }}
    >
      <div className="flex h-full flex-col bg-white">
        <div className="flex items-start justify-between border-b border-gray-200 px-4 py-4">
          <div>
            <div className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <FolderClock className="h-4 w-4 text-[#1D5176]" aria-hidden="true" />
              <span>History</span>
            </div>
            <div className="mt-1 text-sm text-gray-500">Projects</div>
          </div>
          <div className="flex items-center gap-1">
            <Tooltip title="Refresh projects">
              <span>
                <IconButton onClick={() => void loadProjects()} disabled={loading} size="small">
                  <RefreshCw className="h-4 w-4" aria-hidden="true" />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton onClick={onClose} size="small">
              <X className="h-4 w-4" aria-hidden="true" />
            </IconButton>
          </div>
        </div>

        <div className="border-b border-gray-200 px-4 py-3">
          <div className="flex flex-wrap gap-2">
            {filterOptions.map((option) => {
              const selected = filter === option.value
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setFilter(option.value)}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                    selected
                      ? 'border-[#1D5176] bg-[#1D5176] text-white'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {option.label} ({option.count})
                </button>
              )
            })}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {loading ? (
            <div className="flex h-full items-center justify-center">
              <CircularProgress size={28} />
            </div>
          ) : error ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-sm text-gray-600">
              <div>{error}</div>
              <Button size="sm" variant="secondary" onClick={() => void loadProjects()}>
                Retry
              </Button>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-gray-500">
              No projects found.
            </div>
          ) : (
            <div className="space-y-3">
              {filteredProjects.map((project) => {
                const projectChanges = getProjectChanges(project)
                const timestamp = getProjectTimestampDetails(project)
                return (
                <div
                  key={project.id}
                  className={`rounded-md border bg-white p-3 shadow-sm ${
                    isCompletedProject(project) ? 'border-gray-200' : 'border-[#B7D3EC]'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="truncate text-sm font-semibold text-gray-900" title={project.name || project.id}>
                          {project.name || project.id}
                        </span>
                        {project.targetRevision && (
                          <span className="shrink-0 rounded bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700">
                            Rev {project.targetRevision}
                          </span>
                        )}
                      </div>
                      {project.description && (
                        <div className="mt-1 line-clamp-2 text-xs text-gray-500">
                          {project.description}
                        </div>
                      )}
                    </div>
                    <span className={`shrink-0 rounded px-2 py-0.5 text-[11px] font-medium ${getStatusClassName(project)}`}>
                      {formatStatusLabel(project.status)}
                    </span>
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                    <div>Change/ECO</div>
                    <div className="text-right text-gray-800">{project.changeEcoNumber || '--'}</div>
                    <div>Created by</div>
                    <div className="text-right text-gray-800">{getProjectCreator(project)}</div>
                    <div>{timestamp.label}</div>
                    <div className="text-right text-gray-800">{timestamp.value}</div>
                    {projectChanges && (
                      <>
                        <div>Changes</div>
                        <div className="text-right text-gray-800">{projectChanges}</div>
                      </>
                    )}
                  </div>

                  <div className="mt-3 flex justify-end border-t border-gray-200 pt-2">
                    <Button size="sm" variant="secondary" onClick={() => onEnterProject(project)}>
                      View
                    </Button>
                  </div>
                </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </Drawer>
  )
}
